"use client";

import * as React from "react";
import { Search, X } from "lucide-react";

import { cn } from "@immi/ui/lib/utils";
import { Input } from "@immi/ui/components/input";

type SearchFieldProps = Omit<
  React.ComponentProps<"input">,
  "value" | "defaultValue" | "onChange" | "type"
> & {
  defaultValue?: string;
  onSearch: (value: string) => void;
  delay?: number;
  containerClassName?: string;
};

function SearchField({
  defaultValue = "",
  onSearch,
  delay = 400,
  placeholder = "Buscar…",
  className,
  containerClassName,
  ...props
}: SearchFieldProps) {
  const [value, setValue] = React.useState(defaultValue);
  const [syncedDefault, setSyncedDefault] = React.useState(defaultValue);
  const onSearchRef = React.useRef(onSearch);

  React.useEffect(() => {
    onSearchRef.current = onSearch;
  });

  // Fonte da verdade é a URL: quando ela muda por fora (back/forward, limpar
  // filtros), o input reflete sem disparar uma nova busca.
  if (syncedDefault !== defaultValue) {
    setSyncedDefault(defaultValue);
    setValue(defaultValue);
  }

  React.useEffect(() => {
    if (value === defaultValue) return;
    const timer = setTimeout(() => onSearchRef.current(value), delay);
    return () => clearTimeout(timer);
  }, [value, defaultValue, delay]);

  const clear = (): void => setValue("");

  return (
    <div
      data-slot="search-field"
      className={cn("relative", containerClassName)}
    >
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-subtle"
      />
      <Input
        type="search"
        role="searchbox"
        value={value}
        placeholder={placeholder}
        onChange={(event) => setValue(event.target.value)}
        className={cn(
          "px-9 [&::-webkit-search-cancel-button]:appearance-none",
          className,
        )}
        {...props}
      />
      {value && (
        <button
          type="button"
          aria-label="Limpar busca"
          onClick={clear}
          className="absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-neutral-100 hover:text-ink-body"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}

export { SearchField };
export type { SearchFieldProps };
